import { useCallback, useRef } from 'react';
import { useStore } from 'zustand';
import { useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStoreActions } from '~/contexts';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useQueryState } from '~/hooks/reactQuery/useQueryState';
import { taskFromJob } from '../taskModel';
import { useTasksDrawerStoreApi } from '../tasksDrawerStore';
const EMPTY = [];
function toAwaitedJob(task, overrides) {
    switch(task.kind){
        case 'relationshipPdf':
            return {
                jobId: task.jobId,
                kind: task.kind,
                agreementName: task.agreementName,
                relationshipId: task.relationshipId,
                overrides
            };
        default:
            {
                const _exhaustive = task.kind;
                return _exhaustive;
            }
    }
}
export const GetTasksJobsTID = (parentTestId)=>getTestIds(parentTestId);
const SILENT_FEEDBACK = {
    kind: 'silent'
};
export function useTasksJobs() {
    let { refreshOnHookMount = false } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    const { api } = useStoreActions();
    const drawerStore = useTasksDrawerStoreApi();
    const openDrawer = useStore(drawerStore, (s)=>s.openDrawer);
    const { showInfoToast } = useOverlayContext();
    const { data, isLoading, isFetching, isFetched, refresh, optimisticUpdate, error } = useQueryState({
        queryKey: [
            QueryKeys.TasksJobs
        ],
        queryFn: async ()=>(await api.jobs.getJobs()).map(taskFromJob),
        refreshOnHookMount
    });
    const optimisticUpdateRef = useRef(optimisticUpdate);
    optimisticUpdateRef.current = optimisticUpdate;
    const stableOptimisticUpdate = useCallback((updater)=>optimisticUpdateRef.current(updater), []);
    const addTask = useCallback(function(task) {
        let feedback = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : SILENT_FEEDBACK, overrides = arguments.length > 2 ? arguments[2] : void 0;
        stableOptimisticUpdate((tasks)=>[
                task,
                ...tasks.filter((t)=>t.jobId !== task.jobId)
            ]);
        drawerStore.getState().registerAwaitedJob(toAwaitedJob(task, overrides));
        const { TasksJobsTestIds } = GetTasksJobsTID();
        const toastTestId = TasksJobsTestIds.InitialToast(task.jobId);
        switch(feedback.kind){
            case 'silent':
                return;
            case 'auto':
                if (drawerStore.getState().isTasksDrawerAvailable) openDrawer();
                else showInfoToast(feedback.message, toastTestId);
                return;
            default:
                {
                    const _exhaustive = feedback;
                    return _exhaustive;
                }
        }
    }, [
        stableOptimisticUpdate,
        drawerStore,
        openDrawer,
        showInfoToast
    ]);
    const removeTask = useCallback((jobId)=>{
        stableOptimisticUpdate((tasks)=>tasks.filter((t)=>t.jobId !== jobId));
        drawerStore.getState().dropAwaitedJob(jobId);
    }, [
        stableOptimisticUpdate,
        drawerStore
    ]);
    return {
        tasks: data !== null && data !== void 0 ? data : EMPTY,
        isLoading,
        isFetching,
        isSettled: isFetched,
        refresh,
        optimisticUpdate: stableOptimisticUpdate,
        addTask,
        removeTask,
        error
    };
}
